import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import LandingPageHeader from "../LandingPage/LandingPageHeader";
import {
  resolveInitialLang,
  type StudyLanguage,
} from "../LandingPage/landingLanguage";
import i18n from "../../i18n";
import http from "../../utils/http";

const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CheckEmail = () => {
  const [searchParams] = useSearchParams();
  const { studySlug } = useParams();
  const email = searchParams.get("email") || "";
  const { t } = useTranslation("auth");
  const [lang, setLang] = useState("en");
  const [studyLangs, setStudyLangs] = useState<StudyLanguage[]>([]);

  useEffect(() => {
    const fetchLanguages = async () => {
      if (!studySlug) {
        const storedLang = localStorage.getItem("lang") || "en";
        setLang(storedLang);
        i18n.changeLanguage(storedLang);
        return;
      }

      try {
        const langRes: AxiosResponse = await http.get(
          `/data/study-languages?studySlug=${studySlug}`,
        );
        const langs: StudyLanguage[] = langRes?.data?.data || [];
        setStudyLangs(langs);

        const storedLang = localStorage.getItem("lang");
        const initialLang = resolveInitialLang(langs, storedLang);
        localStorage.setItem("lang", initialLang);
        setLang(initialLang);
        i18n.changeLanguage(initialLang);
      } catch {
        const storedLang = localStorage.getItem("lang") || "en";
        setLang(storedLang);
        i18n.changeLanguage(storedLang);
      }
    };

    fetchLanguages();
  }, [studySlug]);

  const handleLanguageChange = useCallback(
    (selectedLang: string) => {
      if (selectedLang === lang) return;
      localStorage.setItem("lang", selectedLang);
      setLang(selectedLang);
      i18n.changeLanguage(selectedLang);
    },
    [lang],
  );

  return (
    <Stack sx={{ height: "100vh" }}>
      <LandingPageHeader
        languages={studyLangs}
        lang={lang}
        onLanguageChange={handleLanguageChange}
      />
      <Divider />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 70px)",
          p: 2,
          bgcolor: "#F9FAFB",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            bgcolor: "white",
            borderRadius: "16px",
            maxWidth: 448,
            width: "100%",
            p: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                bgcolor: "rgba(58, 30, 109, 0.1)",
                color: "rgb(58, 30, 109)",
              }}
            >
              <EmailIcon />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#111827",
                mb: 1,
              }}
            >
              {t("CHECK_EMAIL_TITLE")}
            </Typography>
            <Typography sx={{ color: "#6B7280", mb: 1, fontSize: "16px" }}>
              {t("CHECK_EMAIL_SENT_TO")}
            </Typography>
            {email && (
              <Typography
                sx={{
                  color: "#581E6D",
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: 2,
                }}
              >
                {email}
              </Typography>
            )}
            <Typography sx={{ color: "#6B7280", fontSize: "0.875rem" }}>
              {t("CHECK_EMAIL_INSTRUCTION")}
            </Typography>
            <Typography
              sx={{
                color: "#6B7280",
                fontSize: "0.75rem",
                mt: 1.5,
                opacity: 0.9,
              }}
            >
              {t("CHECK_EMAIL_SPAM_HINT")}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
};

export default CheckEmail;
